<template>
  <div class="system-parameter-form-container">
    <div v-if="isLoading" class="loading-spinner">
      <p><i class="fas fa-spinner fa-spin"></i> 載入參數資料...</p>
    </div>

    <form v-else @submit.prevent="submitForm" class="edit-form">
      <div class="form-group">
        <label for="paramKey">參數鍵 <span class="required">*</span></label>
        <input 
          type="text" 
          id="paramKey" 
          v-model="parameter.paramKey" 
          required 
          class="form-input"
          :disabled="isEditMode"
          placeholder="請輸入參數鍵（最多100字元）"
          maxlength="100"
        >
        <small v-if="isEditMode" class="form-hint">編輯模式下參數鍵不可修改</small>
      </div>

      <div class="form-group">
        <label for="paramValue">參數值 <span class="required">*</span></label>
        <textarea 
          id="paramValue" 
          v-model="parameter.paramValue" 
          required 
          class="form-textarea"
          placeholder="請輸入參數值"
          rows="4"
        ></textarea>
      </div>

      <div class="form-actions">
        <button type="button" class="cancel-btn" @click="handleCancel">
          <i class="fas fa-times"></i> 取消
        </button>
        <button type="submit" class="submit-btn" :disabled="isSubmitting">
          <i v-if="!isSubmitting" class="fas fa-save"></i>
          <i v-else class="fas fa-spinner fa-spin"></i>
          {{ isSubmitting ? '儲存中...' : (isEditMode ? '更新參數' : '新增參數') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, defineProps, defineEmits } from 'vue';
import { useToast } from 'vue-toastification';
import { 
  createSystemParameter, 
  updateSystemParameter, 
  getSystemParameterById 
} from '@/api/systemParameter.api.js';

const props = defineProps({
  parameterData: {
    type: Object,
    default: null
  },
  isEditMode: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'saved']);

const toast = useToast();
const isLoading = ref(false);
const isSubmitting = ref(false);

const parameter = reactive({
  paramKey: '',
  paramValue: ''
});

const loadParameterData = async () => {
  if (props.isEditMode && props.parameterData) {
    if (props.parameterData.id) {
      isLoading.value = true;
      try {
        const data = await getSystemParameterById(props.parameterData.id);
        parameter.paramKey = data.paramKey || '';
        parameter.paramValue = data.paramValue || '';
      } catch (error) {
        toast.error('載入參數資料失敗');
        emit('close');
      } finally {
        isLoading.value = false;
      }
    } else {
      parameter.paramKey = props.parameterData.paramKey || '';
      parameter.paramValue = props.parameterData.paramValue || '';
    }
  }
};

const submitForm = async () => {
  if (isSubmitting.value) return;

  if (!parameter.paramKey.trim()) {
    toast.error('請輸入參數鍵');
    return;
  }

  if (!parameter.paramValue.trim()) {
    toast.error('請輸入參數值');
    return;
  }

  isSubmitting.value = true;

  try {
    const submitData = {
      paramKey: parameter.paramKey.trim(),
      paramValue: parameter.paramValue.trim()
    };

    if (props.isEditMode) {
      await updateSystemParameter(props.parameterData.id, submitData);
      toast.success('系統參數更新成功！');
    } else {
      await createSystemParameter(submitData);
      toast.success('系統參數新增成功！');
    }

    emit('saved');
  } catch (error) {
    const errorMsg = error.response?.data?.message || '操作失敗';
    toast.error(`${props.isEditMode ? '更新' : '新增'}系統參數失敗：${errorMsg}`);
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  emit('close');
};

onMounted(() => {
  loadParameterData();
});
</script>

<style scoped>
.system-parameter-form-container {
  background-color: rgba(30, 30, 40, 0.9);
  border: 1px solid rgba(160, 100, 220, 0.3);
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
}

.loading-spinner {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
  color: #888e95;
}

.edit-form {
  max-width: 600px;
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #e0e0e0;
  font-size: 0.95em;
}

.required {
  color: #ff5555;
}

.form-input, .form-textarea {
  width: 100%;
  padding: 12px 15px;
  background-color: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(160, 100, 220, 0.4);
  border-radius: 5px;
  color: #e0e0e0;
  font-size: 1em;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #ff8c69;
  box-shadow: 0 0 8px rgba(255, 140, 105, 0.3);
}

.form-input:disabled {
  background-color: rgba(40, 40, 50, 0.5);
  color: #a0a0a0;
  cursor: not-allowed;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.form-hint {
  display: block;
  margin-top: 5px;
  font-size: 0.85em;
  color: #a0a0b0;
  font-style: italic;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(160, 100, 220, 0.2);
}

.cancel-btn, .submit-btn {
  padding: 12px 24px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1em;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cancel-btn {
  background-color: rgba(80, 80, 90, 0.7);
  color: #c0c0c0;
  border: 1px solid rgba(100,100,110,0.8);
}

.cancel-btn:hover {
  background-color: rgba(100, 100, 110, 0.8);
}

.submit-btn {
  background: linear-gradient(145deg, #ff8c69, #e06c55);
  color: #ffffff;
}

.submit-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, #e06c55, #cc5a47);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .system-parameter-form-container {
    padding: 20px;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .cancel-btn, .submit-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>